

































































import { defineComponent } from '@vue/composition-api';
import ERPRepository from '@/repositories/ERPRepository';
import AccountsLogRepository from '@/repositories/AccountsLogRepository';
import DialogHelper from '@/helpers/DialogHelper';
import { VForm } from '@/types/VForm';
import ERPEventsEnum from '@/domain/enums/ERPEventsEnum';
import AccountActionsEnum from '@/repositories/parameters/AccountLogs/enums/AccountActionsEnum';
import PaymentRemittance from '@/domain/models/PaymentRemittance';
import PaymentRemittancesActionType from '@/domain/enums/PaymentRemittancesActionType';
import { CloseActionData } from '../../utils/interfaces';

export default defineComponent({
  name: 'ChangeDateDialog',
  props: {
    open: {
      required: true,
      type: Boolean,
    },
    items: {
      required: true,
      type: Array as () => Array<PaymentRemittance>,
    },
  },

  data() {
    const erpRepository: ERPRepository = new ERPRepository();
    const accountsLogRepository: AccountsLogRepository = new AccountsLogRepository();

    const date: string = '';
    const loading: boolean = false;
    const isOpen: boolean = this.open;

    return {
      erpRepository,
      accountsLogRepository,
      date,
      loading,
      isOpen,
    };
  },
  computed: {
    dialogWidth(): string {
      return DialogHelper.getDialogWidth(this.$vuetify.breakpoint, {
        smAndDown: '100%',
        md: '60%',
        default: '40%',
      });
    },
    companyGroupId(): number {
      return Number(this.$session.get('company_group_id'));
    },
  },
  watch: {
    open(): void {
      this.isOpen = this.open;
      if (this.open && this.items.length > 0) {
        this.date = this.items[0].due_date || '';
      }
    },
  },

  methods: {
    async changeDate(): Promise<void> {
      try {
        this.loading = true;
        const item = this.items[0];
        const useNCC = Boolean(this.$session.get('use_ncc')) && item.type === 'NCC';
        const tableName = useNCC ? 'account_receivable' : 'account_payable';
        const response = await Promise.allSettled([
          this.updateExternalERP(item, tableName),
          this.updateInternalCustomerERP(item, useNCC),
          this.upsertInternalErp(item, useNCC),
        ]);

        if (!response.every(({ status }) => status === 'fulfilled')) {
          throw new Error();
        }

        await this.accountsLogRepository.createLog(
          this.companyGroupId,
          {
            type: useNCC ? 'receivable' : 'payable',
            company_id: item.company_id,
            action: AccountActionsEnum.CHANGE_DATE,
            content: 'Data atualizada com sucesso.',
            id_customer: item.id_customer,
          },
        );

        this.$notification.success('Data alterada com sucesso!');
        this.close(true);
      } catch (error) {
        this.$notification.error('Houve um problema ao alterar a data.');
      } finally {
        this.loading = false;
      }
    },

    async updateExternalERP(item: PaymentRemittance, tableName: string): Promise<void> {
      await this.erpRepository.updateExternalERP(
        this.companyGroupId,
        item.company_id,
        {
          id: item.id_customer,
          event: ERPEventsEnum.CASH_FLOW_DATE,
          data: {
            cash_flow: this.date.replaceAll('-', ''),
            type: tableName,
          },
        },
      );
    },

    async updateInternalCustomerERP(item: PaymentRemittance, useNCC: boolean): Promise<void> {
      const fieldBasedOnTable = useNCC ? 'e1_zdtflux' : 'e2_zdtflux';
      const identifyValue = useNCC ? 'CR' : 'CP';

      await this.erpRepository.updateInternalERP({
        id: item.id,
        field: fieldBasedOnTable,
        identify_values: identifyValue,
        value: this.date,
      });
    },

    async upsertInternalErp(item: PaymentRemittance, useNCC: boolean): Promise<void> {
      const identifyValue = useNCC ? 'CR' : 'CP';

      await this.erpRepository.saveDataInternalTable({
        _identifyValue: identifyValue,
        _field: 'data',
        _date: item.due_date,
        _keyId: item.id_customer,
        _data: this.date,
      });
    },

    close(reload = false): CloseActionData {
      const closeData: CloseActionData = {
        type: PaymentRemittancesActionType.CHANGE_DATE,
        reload,
      };
      this.$emit('close', closeData);
      return closeData;
    },

    handleChangeDate(): void {
      const changeDateForm = this.$refs.ChangeDateForm as VForm;

      if (!changeDateForm.validate()) {
        this.$notification.error('Há campos preenchidos incorretamente!');
        return;
      }

      this.changeDate();
    },
  },
});
